import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { TestsApi } from '../../services/testsApi';
import { TagInput, errorText } from './dialogs';

export interface TesterSnapshot {
  functionId: string;
  functionName: string;
  /** A library item the input came from, when it wasn't edited. */
  materialId?: string | null;
  materialName?: string | null;
  input?: unknown;
  inputType: string;
  message?: string;
  output: unknown;
}

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Couldn't read the file"));
    reader.readAsDataURL(file);
  });

/** What the tester holds, as a case input: library item, attached file, data URL, JSON or text. */
export async function testerSnapshot(args: {
  functionId: string;
  functionName: string;
  material?: { id: string; name: string; data: { content_type: string; content?: string } } | null;
  file?: File | null;
  input: string;
  inputType: string;
  additionalInstructions: string;
  output: unknown;
}): Promise<TesterSnapshot> {
  const { material, file, input, inputType, additionalInstructions } = args;
  const base = { functionId: args.functionId, functionName: args.functionName, inputType, output: args.output };
  if (file) return { ...base, input: await readAsDataUrl(file), message: input.trim() || undefined };
  const isFileKind = !['text', 'json'].includes(inputType);
  if (material) {
    const textKind = ['text', 'json'].includes(material.data.content_type);
    const unchanged = textKind ? input === (material.data.content || '') : isFileKind && input.startsWith('data:');
    if (unchanged) {
      return { ...base, materialId: material.id, materialName: material.name, message: isFileKind ? additionalInstructions.trim() || undefined : undefined };
    }
  }
  if (isFileKind && input.startsWith('data:')) return { ...base, input, message: additionalInstructions.trim() || undefined };
  if (inputType === 'json') {
    try {
      return { ...base, input: JSON.parse(input) };
    } catch {
      return { ...base, input };
    }
  }
  return { ...base, input };
}

export const SaveTestCaseDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  snapshot: TesterSnapshot | null;
  onClose: () => void;
}> = ({ open, api, snapshot, onClose }) => {
  const [name, setName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [useOutput, setUseOutput] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ functionId: string } | null>(null);

  useEffect(() => {
    if (!open || !snapshot) return;
    const text = typeof snapshot.input === 'string' && !snapshot.input.startsWith('data:') ? snapshot.input : '';
    setName(snapshot.materialName || (text ? text.slice(0, 60) : `${snapshot.functionName} ${snapshot.inputType} case`));
    setTags([]);
    setUseOutput(true);
    setError(null);
    setSaved(null);
  }, [open, snapshot]);

  const save = async () => {
    if (!snapshot) return;
    setSaving(true);
    setError(null);
    try {
      await api.caseFromTester({
        function_id: snapshot.functionId,
        name: name.trim(),
        input: snapshot.materialId ? undefined : snapshot.input,
        input_type: snapshot.inputType,
        message: snapshot.message,
        material_ids: snapshot.materialId ? [snapshot.materialId] : [],
        output: snapshot.output,
        use_output_as_expected: useOutput,
        tags,
      });
      setSaved({ functionId: snapshot.functionId });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save as test case</DialogTitle>
      <DialogContent>
        {saved ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            Saved and enabled on {snapshot?.functionName}.{' '}
            <Link component={RouterLink} to={`/dashboard/function-runs/${saved.functionId}?tab=tests`}>
              Open its tests
            </Link>{' '}
            to review the expected output or compare models.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2" color="text.secondary">
              Saves this input to the Test Library and enables it on {snapshot?.functionName}
              {snapshot?.materialName ? `, using the library item "${snapshot.materialName}"` : ''}.
            </Typography>
            <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth autoFocus />
            <TagInput value={tags} onChange={setTags} />
            <FormControlLabel
              control={<Checkbox checked={useOutput} onChange={e => setUseOutput(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Use this response as the expected output (you can edit it later)
                </Typography>
              }
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{saved ? 'Done' : 'Cancel'}</Button>
        {!saved && (
          <Button variant="contained" onClick={save} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
